/**
 * useCapexRates - Hook to fetch admin-managed CapEx rates
 *
 * Fetches the default pricing structure from the backend and provides
 * helper functions to look up rates by category and item ID.
 *
 * NO FALLBACKS: If rates cannot be fetched, an error is thrown.
 * The system must have valid CapEx rates configured to function.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../auth/useAuth';
import { API_BASE_URL } from '../services/api';

function useCapexRates() {
    const { currentUser } = useAuth();
    const [capexStructure, setCapexStructure] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch CapEx rates from the API
    const fetchRates = useCallback(async () => {
        if (!currentUser) {
            setLoading(false);
            return;
        }

        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/capex-rates`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) {
                throw new Error('Failed to fetch CapEx rates');
            }

            const data = await res.json();
            setCapexStructure(data);
            setError(null);
        } catch (err) {
            console.error('Error fetching CapEx rates:', err);
            const errorMsg = `Failed to fetch CapEx rates: ${err.message}. System cannot calculate costs without valid rates.`;
            setError(errorMsg);
            setCapexStructure(null);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        fetchRates();
    }, [fetchRates]);

    // Convert the array-based structure to a lookup map for easy access
    const ratesLookup = useMemo(() => {
        if (!capexStructure?.categories) {
            // Only log error if loading is complete but no data
            // Don't log during initial load - that's expected
            return {};
        }

        const lookup = {};
        capexStructure.categories.forEach(category => {
            lookup[category.id] = {};
            category.items?.forEach(item => {
                lookup[category.id][item.id] = {
                    rate: item.rate,
                    unit: item.unit,
                    name: item.name,
                    description: item.description
                };
            });
        });
        return lookup;
    }, [capexStructure]);

    // Helper: Get a specific rate by category and item ID
    const getRate = useCallback((categoryId, itemId) => {
        const category = ratesLookup[categoryId];
        if (!category) return null;
        return category[itemId] || null;
    }, [ratesLookup]);

    // Helper: Get Solar PV turnkey rate ($/kW) - returns null if not available
    const pvTurnkeyRate = useMemo(() => {
        const rate = getRate('solar', 'pv_turnkey');
        return rate?.rate || null;
    }, [getRate]);

    // Helper: Get battery equipment rate ($/kWh) - returns null if not available
    const batteryEquipmentRate = useMemo(() => {
        const rate = getRate('equipment', 'battery_equipment');
        return rate?.rate || null;
    }, [getRate]);

    // Helper: Get battery power electronics rate ($/kW) - returns null if not available
    const batteryPowerRate = useMemo(() => {
        const rate = getRate('equipment', 'battery_power');
        return rate?.rate || null;
    }, [getRate]);

    // Helper: Get battery installation rate ($/kWh) - returns null if not available
    const batteryInstallRate = useMemo(() => {
        const rate = getRate('installation', 'battery_install');
        return rate?.rate || null;
    }, [getRate]);

    // Helper: Get generator equipment rate ($/kW) - returns null if not available
    const generatorEquipmentRate = useMemo(() => {
        const rate = getRate('equipment', 'generator');
        return rate?.rate || null;
    }, [getRate]);

    // Helper: Get generator installation rate ($/kW) - returns null if not available
    const generatorInstallRate = useMemo(() => {
        const rate = getRate('installation', 'generator_install');
        return rate?.rate || null;
    }, [getRate]);

    // Helper: Get switchgear rate ($/system) - returns null if not available
    const switchgearRate = useMemo(() => {
        const rate = getRate('equipment', 'switchgear');
        return rate?.rate || null;
    }, [getRate]);

    // Helper: Get all soft cost percentages - returns null values if incomplete
    const softCostRates = useMemo(() => {
        const engineering = getRate('soft_costs', 'engineering');
        const permits = getRate('soft_costs', 'permits');
        const projectManagement = getRate('soft_costs', 'project_management');
        const insurance = getRate('soft_costs', 'insurance');
        const contingency = getRate('soft_costs', 'contingency');
        const profit = getRate('soft_costs', 'profit');

        return {
            engineering: engineering?.rate || null,
            permits: permits?.rate || null,
            projectManagement: projectManagement?.rate || null,
            insurance: insurance?.rate || null,
            contingency: contingency?.rate || null,
            profit: profit?.rate || null
        };
    }, [getRate]);

    // Check if all essential rates are loaded
    const hasValidRates = !!(
        pvTurnkeyRate &&
        batteryEquipmentRate &&
        batteryPowerRate &&
        batteryInstallRate &&
        generatorEquipmentRate &&
        generatorInstallRate &&
        switchgearRate &&
        softCostRates.engineering &&
        softCostRates.permits &&
        softCostRates.projectManagement &&
        softCostRates.contingency
    );

    return {
        // Data
        capexStructure,
        ratesLookup,
        loading,
        error,
        hasValidRates, // Flag to check if all rates loaded successfully

        // Helpers
        getRate,
        refetch: fetchRates,

        // Common rates (for convenience) - may be null if not loaded
        pvTurnkeyRate,
        batteryEquipmentRate,
        batteryPowerRate,
        batteryInstallRate,
        generatorEquipmentRate,
        generatorInstallRate,
        switchgearRate,
        softCostRates
    };
}

export default useCapexRates;
